import React from 'react';
import { AlertTriangle } from 'lucide-react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /**
   * Rendered instead of the default card. Receives a reset that re-mounts the
   * subtree, for callers that can offer something better than "try again".
   */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Stops one broken screen from taking the whole app down.
 *
 * Without a boundary anywhere in the tree, React unmounts the entire root on a
 * render error: the page goes white, the header goes with it, and browser Back
 * does not bring it back because there is no longer an app listening to the
 * history. A single malformed API response — one endpoint returning an object
 * where a component maps over an array — is enough to do it.
 *
 * Placed INSIDE Layout so the header survives and the user can navigate away,
 * and keyed on the pathname by its caller so moving to another route clears a
 * caught error instead of pinning it there.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    // No error reporting service is wired up; the console is what a developer
    // reaching for this actually has.
    console.error('Unhandled render error:', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div className="px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-lg rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-red-500" />
          <p className="font-medium text-red-900">This page ran into a problem</p>
          <p className="mt-1 text-sm text-red-800">
            The rest of the app still works — use the menu above to go somewhere else, or try
            this page again.
          </p>
          {/* The message, not a stack: it is usually the one line that says
              which shape was wrong, and this app has no error reporting to send
              it anywhere else. */}
          <p className="mt-3 break-words font-mono text-xs text-red-700">{error.message}</p>
          <div className="mt-4 flex justify-center gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-800 hover:bg-red-100"
            >
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
